"use client";
import React, { useContext, createContext, useEffect, useLayoutEffect, useState } from "react";

import useGetExchangeRates from "@/hooks/useGetExchangeRates";
import useProxies from "@/hooks/useProxies";
import { useAuth } from "@clerk/nextjs";
import isEqual from "lodash/isEqual";
import { tokenService } from "@/lib/client/tokenService";
import { useTokenBudgetStore } from "@/stores/tokenBudgetStore";
import { useFiltersStore } from "@/stores/filtersStore";
import { defaultFilters } from "@/lib/shared/constants";
import { loadItems } from "@/lib/client/api";
import { useAuthContext } from "@/context/authContext";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

type DefaultTotal = {
  total: number;
  minPrice: number;
  maxPrice: number;
  avgPrice?: number;
};

type GlobalContextType = {
  exchangeRates?: Currency;
  vendors?: VendorFilter[];
  brands?: BrandFilter[];
  proxies?: ProxyCountry[];
  styles?: {
    [key: string]: number[];
  };
  defaultTotal?: DefaultTotal;
};

const GlobalContext = createContext<GlobalContextType>({
  exchangeRates: {},
  vendors: [],
  brands: [],
  proxies: [],
  styles: {},
});

const hasFiltersStorageHydrated = () => useFiltersStore.persist?.hasHydrated?.() ?? true;

const waitForFiltersStorageHydration = () => {
  let unsubscribe: (() => void) | undefined;

  const promise = new Promise<void>((resolve) => {
    if (hasFiltersStorageHydrated()) {
      resolve();
      return;
    }

    const resolveHydration = () => {
      unsubscribe?.();
      unsubscribe = undefined;
      resolve();
    };

    unsubscribe = useFiltersStore.persist?.onFinishHydration?.(resolveHydration);

    if (!unsubscribe || hasFiltersStorageHydrated()) {
      resolveHydration();
    }
  });

  return {
    promise,
    unsubscribe: () => unsubscribe?.(),
  };
};

const isDefaultSearchRoute = () => {
  if (typeof window === "undefined" || window.location.pathname !== "/search") {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return !params.has("filterid") && !params.has("preset");
};

const shouldWarmDefaultTotal = () => {
  if (!isDefaultSearchRoute()) {
    return false;
  }

  const { activeFilters, searchText, excludeRelists, excludeAuctions, daysBack, kurboMode } =
    useFiltersStore.getState();

  return (
    !searchText &&
    !excludeRelists &&
    !excludeAuctions &&
    !daysBack &&
    !kurboMode &&
    isEqual(activeFilters, defaultFilters)
  );
};

const mergeVendorsWithMetadata = (
  vendors: VendorFilter[] | undefined,
  user: { publicMetadata?: Record<string, unknown> } | null | undefined
) => {
  const metadataVendors = user?.publicMetadata?.vendors;
  if (!Array.isArray(metadataVendors) || !metadataVendors?.length) {
    return vendors ?? [];
  }

  return [...metadataVendors, ...(vendors || [])];
};

export function GlobalDataProvider({ children }: { children: React.ReactNode }) {
  const { exchangeRates } = useGetExchangeRates();
  const { proxiesByCountry } = useProxies();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { user } = useAuthContext();

  const [filters, setFilters] = useState<FilterResponse | undefined>(undefined);
  const [defaultTotal, setDefaultTotal] = useState<DefaultTotal | undefined>(undefined);

  // Descendant passive effects (for example NotificationBell's unread-count
  // request) can run before a parent passive effect. Initialize during the
  // layout phase so authenticated clients always see Clerk's token getter.
  useLayoutEffect(() => {
    tokenService.initialize(getToken);
  }, [getToken]);

  // Warm the token budget store on sign-in so KurboButton shows the correct
  // disabled state before the user makes their first token-consuming request.
  // On sign-out, disable KURBO so signed-out searches don't hit the token guard.
  //
  // IMPORTANT: bail out while Clerk is still loading (isLoaded=false). During
  // loading, isSignedIn is undefined — !undefined is truthy — which previously
  // caused setKurboMode(false) to fire on every page load before auth resolved,
  // stomping on the user's persisted KURBO preference.
  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      useFiltersStore.getState().actions.setKurboMode(false);
      return;
    }
    getToken().then((token) => {
      if (!token) return;
      fetch(`${API_URL}/api/v2/user/tokens`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (res.ok && res.headers.get("X-Tokens-Used") !== null) {
            useTokenBudgetStore.getState().actions.updateFromHeaders(res.headers);
            // Do NOT call setKurboMode here — the user's KURBO preference is
            // persisted in localStorage and must not be overridden on load.
          }
        })
        .catch(() => {});
    });
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    let isCancelled = false;
    const hydrationWait = waitForFiltersStorageHydration();

    type ApiResponse<T> = {
      success: boolean;
      data: T;
    };

    const loadGlobalData = async () => {
      try {
        const filtersResPromise = fetch(`${API_URL}/api/v2/filters`);
        await hydrationWait.promise;

        const filtersRes = await filtersResPromise;

        if (!filtersRes.ok) {
          return;
        }

        const filtersJson = (await filtersRes.json()) as ApiResponse<FilterResponse>;
        if (isCancelled) {
          return;
        }

        let defaultTotalResult: DefaultTotal | undefined;

        if (shouldWarmDefaultTotal()) {
          const { activeFilters } = useFiltersStore.getState();
          const statsResult = await loadItems({
            query: activeFilters,
            searchText: "",
            limit: 1,
            totalOnly: true,
          });

          if (statsResult) {
            defaultTotalResult = {
              total: statsResult.total ?? 0,
              minPrice: statsResult.minPrice ?? 0,
              maxPrice: statsResult.maxPrice ?? Infinity,
              avgPrice: statsResult.avgPrice,
            };
          }
        }

        if (isCancelled) {
          return;
        }

        if (filtersJson.success) {
          setFilters(filtersJson.data);
        }

        if (defaultTotalResult) {
          setDefaultTotal(defaultTotalResult);
        }
      } catch {
        // Keep default empty state when initial client fetch fails.
      }
    };

    loadGlobalData();

    return () => {
      isCancelled = true;
      hydrationWait.unsubscribe();
    };
  }, []);

  const value = React.useMemo(
    () => ({
      exchangeRates,
      vendors: mergeVendorsWithMetadata(filters?.vendors, user),
      brands: filters?.brands,
      proxies: proxiesByCountry,
      defaultTotal,
    }),
    [exchangeRates, filters, user, proxiesByCountry, defaultTotal]
  );

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
}

export function useGlobalData() {
  return useContext(GlobalContext);
}
